// Plan & Billing tab — agent-facing subscription management.
//
// Three sections:
//   1. CURRENT PLAN — what they're on right now + trial countdown + a
//      "Manage subscription" button if they already have a Stripe customer
//      (opens the Stripe Customer Portal for cancel/update card/etc.).
//   2. CHOOSE YOUR PLAN — Solo / Agency cards. Selecting one starts a
//      Stripe Checkout for that plan's priceId.
//   3. ADD-ONS — Extra Seat, Extra Agent, +100 Clients, +500 Signatures,
//      +500 Doc Requests. Same Stripe Checkout flow.
//
// A product whose Stripe priceId env var isn't set yet comes back from
// /products with available:false. The card renders disabled with a
// "Coming soon" badge — so we can ship this screen before Stripe Live
// products are created.

import { useEffect, useState } from 'react';
import {
  listBillingProducts,
  createBillingCheckout,
  createBillingPortal,
  getMyReferrals,
} from '../api.js';

export function BillingTab({ lang, currentUser }) {
  const es = lang === 'es';
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(null); // priceId being acted on
  const [interval, setInterval] = useState('month'); // 'month' | 'year'

  useEffect(() => {
    let cancelled = false;
    listBillingProducts(interval)
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) setErr(e.message || String(e)); });
    return () => { cancelled = true; };
  }, [interval]);

  const handleCheckout = async (priceId, label) => {
    if (!priceId) return;
    setBusy(priceId);
    setErr(null);
    try {
      const { url } = await createBillingCheckout(priceId);
      if (url) window.location.assign(url);
    } catch (e) {
      setErr(e.message || (es ? `Error iniciando checkout de ${label}` : `Failed to start checkout for ${label}`));
      setBusy(null);
    }
  };

  const handlePortal = async () => {
    setBusy('portal');
    setErr(null);
    try {
      const { url } = await createBillingPortal();
      if (url) window.location.assign(url);
    } catch (e) {
      setErr(e.message || (es ? 'Error abriendo Stripe Portal' : 'Failed to open Stripe Portal'));
      setBusy(null);
    }
  };

  if (!data && !err) {
    return <div style={{ color: 'var(--c-text3,#45688a)', fontSize: '.85rem', padding: '2rem', textAlign: 'center' }}>
      {es ? 'Cargando…' : 'Loading…'}
    </div>;
  }
  if (err && !data) {
    return <div style={errBoxStyle}>{err}</div>;
  }

  const { current, plans, addons } = data;
  const isPlatformInternal = currentUser?._backend?.agency?.isPlatformInternal;

  return (
    <div style={{ maxWidth: 900 }}>
      {err && <div style={errBoxStyle}>{err}</div>}

      {/* CURRENT PLAN */}
      <Section title={es ? 'Tu plan' : 'Your plan'}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: '1.35rem', fontWeight: 800, color: 'var(--c-white,#eef6ff)', fontFamily: 'var(--font-display)' }}>
              {planLabel(current.plan, es)}
            </div>
            <SubscriptionStatusLine current={current} es={es} />
          </div>
          <div style={{ display: 'flex', gap: '.5rem', flexWrap: 'wrap' }}>
            {current.hasStripeCustomer && (
              <button
                onClick={handlePortal}
                disabled={busy === 'portal'}
                style={btnSecondary(busy === 'portal')}
              >
                {busy === 'portal' ? (es ? 'Abriendo…' : 'Opening…') : (es ? 'Administrar facturación' : 'Manage billing')}
              </button>
            )}
          </div>
        </div>
        {isPlatformInternal && (
          <div style={internalBadgeStyle}>
            {es
              ? 'Esta agencia es interna de Deskte — no se facturará por Stripe.'
              : 'This is a Deskte-internal agency — Stripe billing is bypassed.'}
          </div>
        )}
      </Section>

      {/* PLANS */}
      <Section title={es ? 'Elige tu plan' : 'Choose your plan'} subtitle={
        es ? 'Cambia o activa tu suscripción.' : 'Switch or activate your subscription.'
      }>
        {/* Monthly / Annual switch. Hidden for Founders: their lifetime rate is
            monthly-only and doesn't stack with the annual discount, so offering
            the toggle would just lead to a row of "not available" cards. */}
        {!current.isFounder && (
          <IntervalToggle value={interval} onChange={setInterval} es={es} />
        )}
        <div style={gridStyle}>
          {plans.map((p) => (
            <ProductCard
              key={p.id}
              p={p}
              es={es}
              busy={!!p.priceId && busy === p.priceId}
              currentPlanId={current.plan}
              onBuy={() => handleCheckout(p.priceId, p.name)}
            />
          ))}
        </div>
        <EnterpriseTeaser es={es} />
      </Section>

      {/* ADD-ONS */}
      <Section title={es ? 'Add-ons' : 'Add-ons'} subtitle={
        es ? 'Compra capacidad extra cuando la necesites.' : 'Buy extra capacity when you need it.'
      }>
        <div style={gridStyle}>
          {addons.map((p) => (
            <ProductCard
              key={p.id}
              p={p}
              es={es}
              busy={!!p.priceId && busy === p.priceId}
              onBuy={() => handleCheckout(p.priceId, p.name)}
            />
          ))}
        </div>
      </Section>

      <ReferralsSection es={es} />

      <div style={{
        fontSize: '.65rem',
        color: 'var(--c-text3,#45688a)',
        textAlign: 'center',
        marginTop: '1rem',
        lineHeight: 1.6,
      }}>
        {es
          ? 'Pagos procesados por Stripe. Cancelas o cambias tu plan cuando quieras desde el portal de facturación.'
          : 'Payments processed by Stripe. Cancel or change your plan anytime from the billing portal.'}
      </div>
    </div>
  );
}

function SubscriptionStatusLine({ current, es }) {
  const baseStyle = { fontSize: '.78rem', color: 'var(--c-text2,#7aa3c0)', marginTop: '.25rem' };
  if (current.inTrial) {
    return (
      <div style={baseStyle}>
        {es ? '🎁 Prueba gratis' : '🎁 Free trial'}
        {' — '}
        <strong style={{ color: current.daysLeft <= 2 ? 'var(--c-amber,#ffb700)' : 'var(--c-cyan,#00d4ff)' }}>
          {current.daysLeft} {es ? (current.daysLeft === 1 ? 'día restante' : 'días restantes') : (current.daysLeft === 1 ? 'day left' : 'days left')}
        </strong>
      </div>
    );
  }
  if (current.subscriptionStatus === 'active') {
    return (
      <div style={baseStyle}>
        <span style={{ color: 'var(--c-green,#00ff88)' }}>● {es ? 'Suscripción activa' : 'Subscription active'}</span>
        {current.cancelAtPeriodEnd && current.currentPeriodEnd && (
          <span style={{ color: 'var(--c-amber,#ffb700)', marginLeft: '.5rem' }}>
            ({es ? 'cancela el' : 'cancels on'} {new Date(current.currentPeriodEnd).toLocaleDateString('en-US')})
          </span>
        )}
      </div>
    );
  }
  if (current.subscriptionStatus === 'past_due') {
    return <div style={{ ...baseStyle, color: 'var(--c-red,#ff3d6b)' }}>● {es ? 'Pago vencido' : 'Payment past due'}</div>;
  }
  if (current.subscriptionStatus === 'canceled') {
    return <div style={baseStyle}>{es ? 'Suscripción cancelada' : 'Subscription cancelled'}</div>;
  }
  // No trial, no subscription → free tier / expired trial
  return (
    <div style={{ ...baseStyle, color: 'var(--c-amber,#ffb700)' }}>
      {es ? 'Sin suscripción activa — elige un plan abajo para continuar.' : 'No active subscription — pick a plan below to continue.'}
    </div>
  );
}

// Monthly / Annual switch. The saving is not hardcoded here — the backend
// reports annualSavingsCents per product — but the headline "-15%" chip is,
// since it's the same figure the marketing site advertises.
function IntervalToggle({ value, onChange, es }) {
  const opt = (key, label) => {
    const on = value === key;
    return (
      <button
        key={key}
        type="button"
        onClick={() => onChange(key)}
        aria-pressed={on}
        style={{
          background: on ? 'var(--c-cyan,#00d4ff)' : 'transparent',
          color: on ? '#06283d' : 'var(--c-text2,#7aa3c0)',
          fontWeight: on ? 800 : 600,
          border: 'none',
          fontSize: '.72rem',
          padding: '.35rem .9rem',
          cursor: 'pointer',
          fontFamily: 'inherit',
          letterSpacing: '.02em',
        }}
      >
        {label}
      </button>
    );
  };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '.6rem', marginBottom: '.9rem' }}>
      <div style={{ display: 'inline-flex', border: '1px solid var(--c-border,#1a3550)', borderRadius: 8, overflow: 'hidden' }}>
        {opt('month', es ? 'Mensual' : 'Monthly')}
        {opt('year', es ? 'Anual' : 'Annual')}
      </div>
      <span style={{
        fontSize: '.6rem',
        fontWeight: 800,
        letterSpacing: '.06em',
        padding: '.12rem .4rem',
        borderRadius: 4,
        background: 'rgba(0,255,136,.12)',
        color: 'var(--c-green,#00ff88)',
      }}>
        -15%
      </span>
    </div>
  );
}

function ProductCard({ p, es, busy, onBuy, currentPlanId }) {
  const isCurrent = currentPlanId === p.id;
  const disabled = !p.available || busy || isCurrent;
  return (
    <div style={{
      background: 'var(--c-surface,#0d1c2e)',
      border: '1px solid var(--c-border,#1a3550)',
      borderRadius: 12,
      padding: '1rem 1.1rem',
      display: 'flex',
      flexDirection: 'column',
      gap: '.6rem',
      position: 'relative',
    }}>
      {isCurrent && (
        <span style={badgeStyle('var(--c-green,#00ff88)')}>
          {es ? 'Tu plan' : 'Current'}
        </span>
      )}
      {!p.available && !isCurrent && (
        <span style={badgeStyle('var(--c-text3,#45688a)')}>
          {es ? 'Próximamente' : 'Coming soon'}
        </span>
      )}
      <div>
        <div style={{ fontWeight: 700, fontSize: '.95rem', color: 'var(--c-white,#eef6ff)' }}>
          {es ? p.nameEs : p.name}
        </div>
        <div style={{ fontSize: '.7rem', color: 'var(--c-text2,#7aa3c0)', marginTop: '.15rem', lineHeight: 1.4 }}>
          {es ? p.descriptionEs : p.description}
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '.25rem', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '1.4rem', fontWeight: 800, color: 'var(--c-white,#eef6ff)' }}>
          {p.priceLabel}
        </span>
        <span style={{ fontSize: '.65rem', color: 'var(--c-text3,#45688a)' }}>
          {p.interval === 'one_time'
            ? (es ? 'pago único' : 'one-time')
            : p.interval === 'year'
              ? (es ? '/año' : '/yr')
              : '/mo'}
        </span>
        {p.annualSavingsCents > 0 && (
          <span style={{ fontSize: '.62rem', fontWeight: 700, color: 'var(--c-green,#00ff88)' }}>
            {es ? 'ahorras' : 'save'} ${(p.annualSavingsCents / 100).toFixed(0)}
          </span>
        )}
        {/* Founder Program: show what they are NOT paying. The backend only
            sets these when the agency is flagged isFounder. */}
        {p.isFounderPrice && p.standardPriceLabel && (
          <>
            <span style={{ fontSize: '.75rem', color: 'var(--c-text3,#45688a)', textDecoration: 'line-through' }}>
              {p.standardPriceLabel}
            </span>
            <span style={{
              fontSize: '.55rem',
              fontWeight: 800,
              letterSpacing: '.06em',
              padding: '.1rem .35rem',
              borderRadius: 4,
              background: 'var(--c-amber-dim)',
              color: 'var(--c-amber,#ffb700)',
            }}>
              FOUNDER
            </span>
          </>
        )}
      </div>
      {/* `busy` lo manda el padre como `busy === p.priceId`, y su estado
          arranca en null. Un producto sin precio configurado trae priceId
          null, asi que la comparacion era null === null: TRUE. Toda tarjeta
          "Proximamente" nacia diciendo "Redirigiendo..." sin que nadie la
          hubiera tocado (Lissette, captura del 2026-09-07). Por eso el padre
          exige ahora que exista el priceId antes de comparar. */}
      <button
        onClick={onBuy}
        disabled={disabled}
        style={btnPrimary(disabled)}
      >
        {isCurrent
          ? (es ? 'Plan actual' : 'Current plan')
          : busy
            ? (es ? 'Redirigiendo…' : 'Redirecting…')
            : !p.available
              ? (es ? 'No disponible' : 'Not available')
              : (es ? `Comprar ${es ? p.nameEs : p.name}` : `Buy ${p.name}`)}
      </button>
    </div>
  );
}

function EnterpriseTeaser({ es }) {
  return (
    <div style={{
      marginTop: '1rem',
      background: 'var(--c-surface2,#112538)',
      border: '1px solid var(--c-border,#1a3550)',
      borderRadius: 10,
      padding: '.85rem 1rem',
      display: 'flex',
      gap: '.75rem',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexWrap: 'wrap',
    }}>
      <div>
        <div style={{ fontWeight: 700, fontSize: '.85rem', color: 'var(--c-white,#eef6ff)' }}>
          {es ? 'Deskte Enterprise' : 'Deskte Enterprise'}
        </div>
        <div style={{ fontSize: '.7rem', color: 'var(--c-text2,#7aa3c0)', marginTop: '.15rem', lineHeight: 1.4 }}>
          {es
            ? 'Agencias grandes y multi-oficina · Acceso API · Onboarding dedicado · BAA personalizado'
            : 'Larger agencies & multi-office teams · API access · Dedicated onboarding · Custom BAA'}
        </div>
      </div>
      <a
        href="mailto:hello@deskte.com?subject=Enterprise%20inquiry"
        style={{
          background: 'transparent',
          border: '1px solid var(--c-cyan,#00d4ff)',
          color: 'var(--c-cyan,#00d4ff)',
          padding: '.5rem .9rem',
          borderRadius: 8,
          fontSize: '.75rem',
          fontWeight: 700,
          textDecoration: 'none',
        }}
      >
        {es ? 'Contactar ventas' : 'Contact sales'}
      </a>
    </div>
  );
}

function planLabel(plan, es) {
  if (plan === 'SOLO') return 'Deskte Solo';
  if (plan === 'AGENCY') return 'Deskte Agency';
  if (plan === 'ENTERPRISE') return 'Deskte Enterprise';
  return plan || (es ? 'Sin plan' : 'No plan');
}

// ────────────────────────────────────────────────────────────────────────────
// REFERIDOS — el agente comparte su enlace y gana sobre su propia mensualidad.
//
// Vive aquí, dentro de Facturación, y no en un módulo aparte del menú: el
// premio ES un descuento en esta misma factura, así que la pantalla donde se
// mira lo que se paga es donde tiene sentido enterarse de cómo pagar menos.
//
// Si el programa está apagado desde Platform Admin, la sección no se pinta —
// mejor nada que un enlace que no va a premiar a nadie.
function ReferralsSection({ es }) {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getMyReferrals()
      .then((d) => { if (!cancelled) setData(d); })
      // Silencioso a propósito: esto es un extra dentro de la pantalla de
      // Facturación. Si el programa falla, la persona tiene que poder seguir
      // pagando su plan sin un error rojo que no le dice nada.
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  if (!data || !data.enabled) return null;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(data.link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) { /* sin portapapeles: el enlace está a la vista para copiarlo a mano */ }
  };

  const t = data.totals;
  const reward = data.reward;

  return (
    <Section
      title={es ? 'Refiere y ahorra' : 'Refer and save'}
      subtitle={es
        ? `Gana ${reward.descriptionEs} por cada agente que traigas, cuando pague su mes ${reward.qualifyAfterPaidInvoices}.`
        : `Earn ${reward.descriptionEn} for every agent you bring, once they pay month ${reward.qualifyAfterPaidInvoices}.`}
    >
      <div style={{
        background: 'var(--c-surface,#0d1c2e)',
        border: '1px solid var(--c-border,#1a3550)',
        borderRadius: 12,
        padding: '.9rem',
      }}>
        <div style={{ fontSize: '.62rem', color: 'var(--c-text3,#45688a)', marginBottom: '.3rem' }}>
          {es ? 'Tu enlace' : 'Your link'}
        </div>
        <div style={{ display: 'flex', gap: '.4rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <code style={{
            flex: '1 1 260px',
            background: 'var(--c-surface2,#112538)',
            border: '1px solid var(--c-border,#1a3550)',
            borderRadius: 8,
            padding: '.45rem .6rem',
            fontSize: '.72rem',
            color: 'var(--c-cyan,#00d4ff)',
            overflowWrap: 'anywhere',
          }}>{data.link}</code>
          <button
            type="button"
            onClick={copy}
            style={{
              background: copied ? 'var(--c-green,#00ff88)' : 'var(--c-cyan,#00d4ff)',
              color: '#0a1628', border: 'none', borderRadius: 8,
              padding: '.45rem .9rem', fontSize: '.75rem', fontWeight: 800,
              fontFamily: 'inherit', cursor: 'pointer',
            }}
          >
            {copied ? (es ? '¡Copiado!' : 'Copied!') : (es ? 'Copiar' : 'Copy')}
          </button>
        </div>
        <div style={{ fontSize: '.66rem', color: 'var(--c-text3,#45688a)', marginTop: '.4rem' }}>
          {es ? 'Tu código: ' : 'Your code: '}<strong style={{ color: 'var(--c-white,#eef6ff)' }}>{data.code}</strong>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit,minmax(110px,1fr))',
          gap: '.5rem',
          marginTop: '.8rem',
          paddingTop: '.8rem',
          borderTop: '1px solid var(--c-border,#1a3550)',
        }}>
          <Stat label={es ? 'Invitados' : 'Invited'} value={t.invited} />
          <Stat label={es ? 'Aún no califican' : 'Not yet qualified'} value={t.pending} />
          <Stat label={es ? 'Por acreditar' : 'Awaiting credit'} value={t.awaitingApproval} amber={t.awaitingApproval > 0} />
          <Stat label={es ? 'Ganado' : 'Earned'} value={t.creditedLabel} green={t.creditedCents > 0} />
        </div>

        {data.referrals.length > 0 && (
          <div style={{ marginTop: '.7rem' }}>
            {data.referrals.map((r) => (
              <div key={r.id} style={{
                display: 'flex', justifyContent: 'space-between', gap: '.5rem',
                fontSize: '.72rem', padding: '.2rem 0',
              }}>
                <span style={{ color: 'var(--c-text2,#7aa3c0)' }}>{r.name}</span>
                <span style={{
                  fontWeight: 700,
                  color: r.status === 'APPLIED'
                    ? 'var(--c-green,#00ff88)'
                    : r.status === 'REJECTED'
                      ? 'var(--c-text3,#45688a)'
                      : r.status === 'PENDING'
                        ? 'var(--c-text3,#45688a)'
                        : 'var(--c-amber,#ffb700)',
                }}>
                  {r.status === 'APPLIED'
                    ? `${es ? 'acreditado' : 'credited'}${r.rewardCents ? ` · $${(r.rewardCents / 100).toFixed(2)}` : ''}`
                    : r.status === 'REJECTED'
                      ? (es ? 'no aplica' : 'not eligible')
                      : r.status === 'PENDING'
                        ? (es ? 'en camino' : 'in progress')
                        : (es ? 'por acreditar' : 'awaiting credit')}
                </span>
              </div>
            ))}
          </div>
        )}

        <div style={{ fontSize: '.63rem', color: 'var(--c-text3,#45688a)', marginTop: '.6rem', lineHeight: 1.5 }}>
          {es
            ? `El premio se acredita a tu próxima factura una vez que tu referido paga su mes ${reward.qualifyAfterPaidInvoices}. Se paga una sola vez por referido.`
            : `The reward lands on your next invoice once your referral pays month ${reward.qualifyAfterPaidInvoices}. Paid once per referral.`}
        </div>
      </div>
    </Section>
  );
}

function Stat({ label, value, amber, green }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{
        fontFamily: 'var(--font-display)',
        fontSize: '1.1rem',
        fontWeight: 800,
        color: green ? 'var(--c-green,#00ff88)' : amber ? 'var(--c-amber,#ffb700)' : 'var(--c-white,#eef6ff)',
      }}>{value}</div>
      <div style={{ fontSize: '.58rem', color: 'var(--c-text3,#45688a)' }}>{label}</div>
    </div>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <div style={{ marginBottom: '1.5rem' }}>
      <div style={{ marginBottom: '.6rem' }}>
        <h3 style={{
          fontFamily: 'var(--font-display)',
          fontSize: '.95rem',
          fontWeight: 700,
          color: 'var(--c-white,#eef6ff)',
          margin: 0,
        }}>{title}</h3>
        {subtitle && (
          <div style={{ fontSize: '.68rem', color: 'var(--c-text3,#45688a)', marginTop: '.15rem' }}>
            {subtitle}
          </div>
        )}
      </div>
      <div style={{
        background: 'var(--c-surface2,#112538)',
        border: '1px solid var(--c-border,#1a3550)',
        borderRadius: 12,
        padding: '1rem 1.1rem',
      }}>
        {children}
      </div>
    </div>
  );
}

const gridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  gap: '.65rem',
};

const errBoxStyle = {
  background: 'rgba(248,113,113,.08)',
  border: '1px solid rgba(248,113,113,.4)',
  color: '#f87171',
  padding: '.6rem .8rem',
  borderRadius: 8,
  fontSize: '.78rem',
  marginBottom: '1rem',
};

const internalBadgeStyle = {
  marginTop: '.6rem',
  background: 'rgba(0,212,255,.06)',
  border: '1px dashed rgba(0,212,255,.4)',
  color: 'var(--c-cyan,#00d4ff)',
  padding: '.45rem .65rem',
  borderRadius: 8,
  fontSize: '.72rem',
};

function badgeStyle(color) {
  return {
    position: 'absolute',
    top: -8,
    right: 10,
    background: color,
    color: '#070d14',
    fontSize: '.55rem',
    fontWeight: 800,
    padding: '2px 7px',
    borderRadius: 6,
    letterSpacing: '0.08em',
  };
}

function btnPrimary(disabled) {
  return {
    width: '100%',
    padding: '.55rem .75rem',
    borderRadius: 8,
    background: disabled ? 'var(--c-surface3,#162d44)' : 'linear-gradient(135deg,#00d4ff,#00b4d8)',
    border: 'none',
    color: disabled ? 'var(--c-text3,#45688a)' : '#070d14',
    fontSize: '.78rem',
    fontWeight: 800,
    fontFamily: 'inherit',
    cursor: disabled ? 'not-allowed' : 'pointer',
    letterSpacing: '.02em',
  };
}

function btnSecondary(disabled) {
  return {
    padding: '.5rem .9rem',
    borderRadius: 8,
    background: 'transparent',
    border: '1px solid var(--c-border,#1a3550)',
    color: 'var(--c-text2,#7aa3c0)',
    fontSize: '.78rem',
    fontWeight: 600,
    fontFamily: 'inherit',
    cursor: disabled ? 'not-allowed' : 'pointer',
  };
}
